import { useEffect, useState } from 'react';
import { BOARD_LEGEND } from '@shared/game/board';
import type { GameMode } from '@shared/game/types';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';

interface RulesModalProps {
  open: boolean;
  onClose: () => void;
  initialMode?: GameMode;
}

type Tab = 'classic' | 'adventure' | 'effects';

export function RulesModal({ open, onClose, initialMode = 'classic' }: RulesModalProps) {
  const [tab, setTab] = useState<Tab>(initialMode);

  useEffect(() => {
    if (open) setTab(initialMode);
  }, [open, initialMode]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manual del Protocolo"
      subtitle="Reglas del Nexo, explicadas sin letra pequeña."
      wide
    >
      <div className="tabs" role="tablist" aria-label="Reglas">
        <button type="button" role="tab" aria-selected={tab === 'classic'} className={tab === 'classic' ? 'is-active' : ''} onClick={() => setTab('classic')}>
          Clásico
        </button>
        <button type="button" role="tab" aria-selected={tab === 'adventure'} className={tab === 'adventure' ? 'is-active' : ''} onClick={() => setTab('adventure')}>
          Aventura
        </button>
        <button type="button" role="tab" aria-selected={tab === 'effects'} className={tab === 'effects' ? 'is-active' : ''} onClick={() => setTab('effects')}>
          Anomalías
        </button>
      </div>

      {tab === 'classic' && (
        <div className="rules">
          <div className="rules__lead">
            <Icon name="sword" size={22} />
            <p>
              Cuatro unidades, un dado y un Núcleo. Gana quien sincroniza sus cuatro fichas con tiradas exactas.
            </p>
          </div>
          <ol className="rules__list">
            <li><strong>Despliegue.</strong> Una ficha sale del muelle cuando sacas un 5. Ese 5 se consume al colocarla en tu plataforma de salida.</li>
            <li><strong>Turnos.</strong> El anfitrión abre la partida y el turno pasa en el sentido del tablero. Si no hay movimientos posibles, el servidor pasa automáticamente.</li>
            <li><strong>Seises.</strong> Un 6 concede otra tirada después de mover. Tres seises consecutivos devuelven a la base tu ficha más avanzada y terminan el turno.</li>
            <li><strong>Capturas.</strong> Si aterrizas en una casilla con fichas rivales, vuelven a su base y repites tirada. No puedes capturar en las casillas de salida ni en los santuarios.</li>
            <li><strong>Zonas seguras.</strong> Las salidas y las zonas seguras protegen a las unidades. Varias unidades del mismo escuadrón pueden compartir celda.</li>
            <li><strong>Barreras.</strong> Dos fichas rivales en la misma casilla no segura forman una barrera: no puedes pasar ni aterrizar en ella. Las barreras no se forman en santuarios.</li>
            <li><strong>Conducto y Núcleo.</strong> Tras completar el recorrido, la ficha entra en su conducto de 7 celdas. Necesitas la tirada exacta para llegar al Núcleo: si te pasas, ese movimiento no es legal.</li>
            <li><strong>Empate técnico.</strong> No hay empate: la primera persona que sincroniza sus cuatro unidades gana.</li>
          </ol>
          <p className="rules__note">
            El servidor es la única autoridad: valida el turno, genera la tirada, comprueba barreras, capturas y efectos. Tu dispositivo no puede cambiar el dado ni las reglas.
          </p>
        </div>
      )}

      {tab === 'adventure' && (
        <div className="rules">
          <div className="rules__lead">
            <Icon name="spark" size={22} />
            <p>
              Las mismas reglas clásicas, con casillas especiales que cambian el ritmo. El anfitrión puede desactivar los
              eventos antes de empezar y jugar exactamente igual que en Clásico.
            </p>
          </div>
          <ol className="rules__list">
            <li><strong>Portal de salto.</strong> Teletransporta 3 celdas adicionales. No captura ni dispara otra anomalía.</li>
            <li><strong>Anomalía de fisura.</strong> Devuelve 3 celdas atrás. Tampoco captura ni encadena anomalías.</li>
            <li><strong>Cofre de anomalías.</strong> El servidor elige un efecto sorpresa de una tabla equilibrada: +2, −2, barrera, tirada extra o estasis.</li>
            <li><strong>Barrera de fase.</strong> Absorbe la próxima captura y se rompe. No se acumula.</li>
            <li><strong>Eventos y capturas.</strong> Los movimientos de un efecto no capturan ni provocan nuevos efectos; solo la tirada del dado puede capturar.</li>
            <li><strong>Aleatoriedad justa.</strong> Las tiradas y los eventos usan el generador criptográfico del servidor. Los dados cosméticos no cambian ninguna probabilidad.</li>
          </ol>
          <div className="rules__weights">
            <h3>Tabla de cofres</h3>
            <div className="weight-row"><span>Vector favorable</span><strong>+2 celdas</strong><em>30%</em></div>
            <div className="weight-row"><span>Deriva de datos</span><strong>−2 celdas</strong><em>30%</em></div>
            <div className="weight-row"><span>Barrera de fase</span><strong>Absorbe 1 captura</strong><em>20%</em></div>
            <div className="weight-row"><span>Sobreimpulso</span><strong>Tirada extra</strong><em>10%</em></div>
            <div className="weight-row"><span>Bucle de estasis</span><strong>Pierdes próxima tirada</strong><em>10%</em></div>
          </div>
        </div>
      )}

      {tab === 'effects' && (
        <div className="rules">
          <div className="rules__lead">
            <Icon name="book" size={22} />
            <p>Pulsa cualquier celda de la arena durante la partida para leer su efecto. Esta es la leyenda completa.</p>
          </div>
          <div className="effect-legend">
            {BOARD_LEGEND.map((entry) => (
              <div key={entry.kind} className={`effect-legend__item effect-legend__item--${entry.kind}`}>
                <span className="effect-legend__icon"><Icon name={entry.icon as IconName} size={20} /></span>
                <div>
                  <strong>{entry.title}</strong>
                  <p>{entry.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="rules__columns">
            <div>
              <h3>Privadas y públicas</h3>
              <p>Las arenas privadas se comparten por enlace o código. Las públicas aparecen en la red y se pueden unir sin código.</p>
            </div>
            <div>
              <h3>2 a 4 jugadores</h3>
              <p>En pareja se usan esquinas opuestas. Con 3 o 4 jugadores se ocupan las esquinas en orden. También puedes añadir bots para probar.</p>
            </div>
            <div>
              <h3>Reconexión</h3>
              <p>Si se cae la conexión, tu asiento se conserva y la partida espera. Al volver, el servidor te devuelve el estado exacto.</p>
            </div>
            <div>
              <h3>Accesibilidad</h3>
              <p>Puedes desactivar animaciones y sonido. El tablero muestra los movimientos legales y también ofrece botones de movimiento.</p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
